import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import EmptyFloorTile, {
	EmptyFloorTileOrientation,
	EmptyFloorTileType,
	getEmptyFloorTileOrientation,
	getEmptyFloorTileType,
} from 'app/src/engine/tiles/EmptyFloorTile';
import Tile from 'app/src/engine/tiles/Tile';
import EmptyFloorTileNode from 'app/src/game/map/tiles/EmptyFloorTile';
import GamePlugin, { TileData } from 'app/src/plugins/GamePlugin';
import * as React from 'react';

export enum EmptyFloorGamePluginError {
	InvalidEmptyFloorTileTypeInTileDescription,
	InvalidEmptyFloorTileOrientationInTileDescription,
}

export default class EmptyFloorGamePlugin extends GamePlugin {
	// Get a list of Tiles that this plugin provides. When the tile grid is
	// being initialized, it'll ask all plugins which Tiles they create, and
	// later use those corresponding plugins to build Tiles.
	public getListOfProvidedTiles(): string[] {
		return ['EmptyFloorTile'];
	}

	// Based on Tile data, generate a Tile.
	public getTile(data: TileData): Tile {
		let type: EmptyFloorTileType | null = null;
		const typeName = data['type'];
		if (typeof typeName === 'string') {
			type = getEmptyFloorTileType(typeName);
		}
		if (type === null) {
			throw EmptyFloorGamePluginError.InvalidEmptyFloorTileTypeInTileDescription;
		}

		let orientation: EmptyFloorTileOrientation | null = null;
		const orientationName = data['orientation'];
		if (typeof orientationName === 'string') {
			orientation = getEmptyFloorTileOrientation(orientationName);
		}
		if (orientation === null) {
			throw EmptyFloorGamePluginError.InvalidEmptyFloorTileTypeInTileDescription;
		}

		const tile = new EmptyFloorTile(type, orientation);

		const wallsData = data['walls'];
		if (Array.isArray(wallsData)) {
			tile.addWallsFromData(wallsData);
		}

		return tile;
	}

	// If the editor chooses to support this plugin, and if this plugin displays
	// tiles, this list of tiles will be used in the side panel.
	public getCanonicalTiles(): Tile[] {
		return [
			new EmptyFloorTile(EmptyFloorTileType.Type1, EmptyFloorTileOrientation.Up),
			new EmptyFloorTile(EmptyFloorTileType.Type2, EmptyFloorTileOrientation.Up),
			new EmptyFloorTile(EmptyFloorTileType.Type3, EmptyFloorTileOrientation.Up),
			new EmptyFloorTile(EmptyFloorTileType.Type4, EmptyFloorTileOrientation.Up),
		];
	}

	// Given a Tile, is this plugin capable of generating a corresponding TileNode?
	public providesNodeForTile(tile: Tile): boolean {
		return tile instanceof EmptyFloorTile;
	}

	// Based on a Tile, generate a TileNode.
	public getTileNode(
		tile: Tile,
		_row: number,
		_col: number,
		_actionResults?: TileActionResult[] | null,
		_completeAnimation?: () => void,
	): React.ReactElement {
		if (!(tile instanceof EmptyFloorTile)) {
			throw new Error('Unsupported Tile.');
		}

		return <EmptyFloorTileNode type={tile.type} orientation={tile.orientation} />;
	}
}
